/**
 * @file BillLineSplitDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-14
 * @modified 2026-10-14
 *
 * One bill line split over several PO lines, by hand (Cam, 14 Oct 2026 — a
 * supplier's one line of 10 T covering PO A's 1 T and PO B's 9 T). Add / Edit
 * Bill opens it from an offer ("Choose the PO lines and quantities") or from
 * an invoice line on no PO line. Each PO line has a tick and the quantity of
 * the bill line it takes, in the bill line's own unit; they must add up to the
 * line's quantity before it splits. What each new line comes to is shown as it
 * will be saved (utils/billLineSplit.splitAmounts — the line's price on every
 * line, amounts and VAT adding up to the line's to the penny).
 *
 * Presentational: the caller applies the answer (utils/billLineSplit.applySplit).
 */

import React, { useMemo, useState } from 'react';
import Modal from './Modal';
import TickCheckbox from './TickCheckbox';
import { DecimalInput } from './OrderLinesGrid';
import { useSlideOutButton } from './SlideOutButton';
import { formatQuantity, formatDecimalInput, roundHalfUp } from '../utils/numberFormatters';
import { formatCurrency } from '../utils/currencyFormatter';
import { lineLabel } from '../utils/billLineReconcile';
import { splitAmounts, rateOf, candidateQtyText } from '../utils/billLineSplit';
import { conversionText } from '../utils/billUnitConversion';
import './BillLineSplitDialog.css';

const EPS = 0.0001;
const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(String(v).replace(/[,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};
const r4 = (v) => roundHalfUp(v, 4) ?? 0;

/** The window's rows from an offer: { [candidate id]: { on, qty } }. */
function rowsFrom(initial) {
  const out = {};
  for (const p of initial?.parts || []) out[p.candidate.id] = { on: true, qty: formatDecimalInput(p.billQty) };
  return out;
}

function SplitBody({ source, candidates, rows, setRow, total, quantity, money, initial }) {
  useSlideOutButton([Math.abs(total - quantity) < EPS]);
  const left = r4(quantity - total);
  return (
    <div className="bill-line-split-body">
      <p className="bill-line-split-intro">
        <strong>{lineLabel(source)}</strong> is one line on the invoice. Tick the PO lines it covers and how much of it
        each takes, in the invoice line&apos;s unit — they must add up to {formatQuantity(quantity)}. Every line keeps
        the invoice&apos;s price, and the amounts and VAT add up to the line&apos;s to the penny.
      </p>
      {initial?.conversion && (
        <p className="bill-line-split-hint">
          <span className="material-symbols-rounded">swap_horiz</span>
          {conversionText(initial.conversion)}
        </p>
      )}
      <div className="table-container">
        <table className="data-table bill-line-split-table">
          <thead>
            <tr>
              <th className="tick-col" aria-label="Split onto" />
              <th>PO</th>
              <th>Line</th>
              <th className="num">PO qty</th>
              <th className="num">Bill qty</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((c) => {
              const row = rows[c.id] || { on: false, qty: '' };
              const at = money ? money.index.get(c.id) : undefined;
              const amount = row.on
                ? (at !== undefined ? money.result.parts[at].net : (num(row.qty) || 0) * (num(source.unit_price) || 0))
                : null;
              return (
                <tr key={c.id} className={row.on ? 'chosen' : ''}>
                  <td className="tick-col">
                    <TickCheckbox
                      id={`bill-line-split-${c.id}`}
                      checked={!!row.on}
                      ariaLabel={`Split onto ${c.number || 'the PO'} ${c.words || c.code || ''}`}
                      onChange={(e) => setRow(c.id, { on: !!e.target.checked })}
                    />
                  </td>
                  <td>{c.number || `PO #${c.poId}`}</td>
                  <td>{c.words || c.code || '—'}{c.code && c.words ? <span className="bill-line-split-code"> · {c.code}</span> : null}</td>
                  <td className="num">
                    {candidateQtyText(c)}
                    <div className="bill-line-split-sub">{c.basis === 'supplied' ? 'supplied' : 'ordered'}</div>
                  </td>
                  <td className="num">
                    <DecimalInput
                      min="0"
                      value={row.qty}
                      disabled={!row.on}
                      onValueChange={(v) => setRow(c.id, { qty: v })}
                      aria-label={`Bill quantity on ${c.number || 'the PO'} ${c.words || ''}`}
                    />
                  </td>
                  <td className="num">{amount === null ? '—' : formatCurrency(amount)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} className="num">Split so far</td>
              <td className={`num${Math.abs(left) < EPS ? ' is-balanced' : ' is-off'}`}>
                {formatQuantity(total)} of {formatQuantity(quantity)}
              </td>
              <td className="num">{money ? formatCurrency(money.result.net) : ''}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {Math.abs(left) >= EPS && (
        <p className="bill-line-split-left" role="status">
          {left > 0 ? `${formatQuantity(left)} still to put on a PO line.` : `${formatQuantity(-left)} too much — take it off a line.`}
        </p>
      )}
      {money && money.result.parts.some(p => p.priceAdjusted || p.vatAdjusted) && (
        <p className="bill-line-split-hint">
          <span className="material-symbols-rounded">info</span>
          {money.result.parts.some(p => p.priceAdjusted)
            ? 'The largest line\'s price moves at the 4th decimal place so the amounts add up to the invoice line to the penny. ' : ''}
          {money.result.parts.some(p => p.vatAdjusted)
            ? 'The largest line carries the penny of VAT rounding so the VAT adds up to the invoice line\'s.' : ''}
        </p>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.source — the bill line (grid shape)
 * @param {Array<object>} props.candidates — utils/billLineSplit gridCandidates / freeCandidates
 * @param {object|null} [props.initial] — utils/billLineSplit.findSplit (its parts start ticked)
 * @param {(split: { sourceId, parts: Array<{ candidate, billQty }> }) => void} props.onApply
 * @param {() => void} props.onClose
 */
function BillLineSplitDialog({ source, candidates = [], initial = null, onApply, onClose }) {
  const [rows, setRows] = useState(() => rowsFrom(initial));
  const quantity = r4(num(source?.quantity) || 0);
  const chosen = candidates.filter(c => rows[c.id]?.on && num(rows[c.id]?.qty) > EPS);
  const total = r4(chosen.reduce((s, c) => s + (num(rows[c.id].qty) || 0), 0));

  const setRow = (id, patch) => setRows((prev) => {
    const was = prev[id] || { on: false, qty: '' };
    const next = { ...was, ...patch };
    // Ticked with nothing in it: what is still left of the line, else the PO line's own.
    if (patch.on && !(num(was.qty) > EPS)) {
      const others = candidates.filter(c => c.id !== id && prev[c.id]?.on)
        .reduce((s, c) => s + (num(prev[c.id].qty) || 0), 0);
      const rest = r4(quantity - others);
      const own = candidates.find(c => c.id === id);
      next.qty = formatDecimalInput(rest > EPS ? rest : (own?.qty || 0));
    }
    return { ...prev, [id]: next };
  });

  // What each chosen line will come to, once the quantities add up.
  const money = useMemo(() => {
    if (chosen.length < 2 || Math.abs(total - quantity) >= EPS) return null;
    const result = splitAmounts({ quantity, unitPrice: source.unit_price, rate: rateOf(source) },
      chosen.map(c => num(rows[c.id].qty)));
    return { result, index: new Map(chosen.map((c, i) => [c.id, i])) };
  }, [chosen, total, quantity, source, rows]);

  const block = chosen.length < 2 ? 'Tick two or more PO lines'
    : Math.abs(total - quantity) >= EPS ? `The quantities come to ${formatQuantity(total)}; the line is ${formatQuantity(quantity)}`
      : null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Split the line across PO lines"
      size="lg"
      className="bill-line-split"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose} title="Leave the line as it is">
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button
              className="btn-slideout btn-slideout-primary"
              disabled={!!block}
              title={block || 'One line per ticked PO line'}
              onClick={() => onApply({ sourceId: source.id, parts: chosen.map(c => ({ candidate: c, billQty: r4(num(rows[c.id].qty)) })) })}
            >
              <div className="icon-wrapper"><span className="material-symbols-rounded">call_split</span></div>
              <span className="btn-label">Split the line</span>
            </button>
          </div>
        </div>
      }
    >
      <SplitBody
        source={source}
        candidates={candidates}
        rows={rows}
        setRow={setRow}
        total={total}
        quantity={quantity}
        money={money}
        initial={initial}
      />
    </Modal>
  );
}

export default BillLineSplitDialog;
